import { clsx } from 'clsx';

import type { ReactNode } from 'react';

import styles from './FormInput.module.css';

import { FeedbackBox, Label } from 'components';

export interface FormInputProps {
  /** The id of the input element. */
  id: string;
  /** label text if empthy the label is not renderd */
  label: string;
  /** label position `top` or `left`. The default is `top`. */
  labelPosition?: 'top' | 'left';
  /** The type of the input element. */
  type?: 'text' | 'password' | 'email' | 'number';
  /** Shows red * */
  required?: boolean;
  /** `true` to color border red to show not valid; otherwise, `false`. */
  notValid?: boolean;
  /** error text to display if set */
  notValidatedText?: string;
  /** Returns array of selected values as number */
  onChange?: (value: string) => void;
  /** Sets max length for the standard input */
  maxLength?: number;
  /** Determines if the container wrapper is rendered. */
  noContainer?: boolean;
  /** Optional custom input that will replace the default input */
  customInput?: ReactNode;
}

export const FormInput = ({
  id,
  label,
  labelPosition = 'top',
  type = 'text',
  required = false,
  notValid = false,
  notValidatedText = '',
  onChange,
  maxLength,
  noContainer = false,
  customInput,
}: FormInputProps) => {
  const blockInvalidChar = (event: React.KeyboardEvent<HTMLInputElement>) =>
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (typeof onChange === 'function') {
      onChange(event.target.value);
    }
  };

  const componentContent = (
    <>
      {label && (
        <Label htmlFor={id} required={required} alignRight>
          {label}
        </Label>
      )}

      <div className={styles['form-input__input-feedback-container']}>
        {customInput ? (
          customInput
        ) : (
          <div className={styles['form-input__input-container']}>
            <input
              id={id}
              type={type}
              {...(type === 'number' && { onKeyDown: blockInvalidChar })}
              onChange={handleChange}
              className={clsx(styles['form-input__input'], {
                [styles['not-valid']]: notValid,
              })}
              {...(maxLength ? { maxLength } : {})}
            />
          </div>
        )}
        {notValidatedText !== '' && <FeedbackBox level="error" feedback={notValidatedText} />}
      </div>
    </>
  );

  return noContainer ? (
    componentContent
  ) : (
    <div className={clsx(styles['form-input__container'], styles[`form-input__container--label-${labelPosition}`])}>
      {componentContent}
    </div>
  );
};
